// Past-session-edit timeline math (next/timeline.md). Pure, no React.
//
// Two problems this module solves, both specific to EDITING a session
// that already happened (live logging is untouched):
//
//  1. Marking a set done while editing a past session used to stamp the
//     live wall clock (`new Date()`), landing the set outside
//     [startedAt, endedAt] and corrupting the history timeline (root
//     causes 1+2). Instead the set is placed "in-window, at tail": just
//     after the latest already-completed set. buildPayload's endedAt
//     re-extension covers the rare case where the tail nudges past the
//     stored end.
//
//  2. Correcting the session start/end via the footer date editor
//     ("ho fatto la sessione 6-7, non 6:15-7:28") must reflow the
//     existing sets proportionally into the new window instead of
//     leaving them stranded outside it.

import type { DraftEntry } from './state/loggerDraftTypes';

// Gap between a newly-completed past-edit set and the previous one. Big
// enough to keep ordering stable across several adds, small enough that
// the set reads as "at the same moment" inside an already-finished
// session.
export const TAIL_STEP_MS = 1000;

// Latest `completedAt` across every set in the draft, in epoch ms.
// Dropset step completions don't drive set ordering (the parent set's
// performedAt does), so they're intentionally ignored here. Returns 0
// when no set carries a completedAt.
export function latestCompletedAtMs(entries: readonly DraftEntry[]): number {
  let latest = 0;
  for (const e of entries) {
    for (const s of e.sets) {
      if (s.completedAt) {
        const ms = Date.parse(s.completedAt);
        if (Number.isFinite(ms) && ms > latest) latest = ms;
      }
    }
  }
  return latest;
}

// completedAt to stamp on a set marked done during a past-session edit:
// `max(latest existing completed set, startedAt) + TAIL_STEP_MS`. Never
// the live clock. The result can exceed a stored endedAt; buildPayload's
// endedAt re-extension is responsible for growing the window to fit.
export function placeCompletedSetAtTail(
  entries: readonly DraftEntry[],
  startedAt: string,
): string {
  const startedMs = Date.parse(startedAt);
  const base = Math.max(
    Number.isFinite(startedMs) ? startedMs : 0,
    latestCompletedAtMs(entries),
  );
  return new Date(base + TAIL_STEP_MS).toISOString();
}

// Proportionally remap one ISO timestamp from an old [s0,e0] window onto
// a new [s1,e1] window, preserving its relative position. Returns the
// input unchanged when either window is degenerate (zero/negative
// length) or the timestamp can't be parsed, so a bad edit can never NaN
// a stored time.
export function rescaleCompletedAt(
  iso: string,
  s0: number,
  e0: number,
  s1: number,
  e1: number,
): string {
  const t = Date.parse(iso);
  const oldLen = e0 - s0;
  const newLen = e1 - s1;
  if (
    !Number.isFinite(t) ||
    !Number.isFinite(oldLen) ||
    !Number.isFinite(newLen) ||
    oldLen <= 0 ||
    newLen <= 0
  ) {
    return iso;
  }
  const mapped = s1 + ((t - s0) * newLen) / oldLen;
  return new Date(mapped).toISOString();
}

// Apply rescaleCompletedAt to every set's completedAt (and each dropset
// step's completedAt, so chains stay attached to their parent) across
// the draft. Sets without a completedAt pass through untouched. Pure —
// returns a new entries array; safe to call from the reducer.
export function rescaleEntriesToWindow(
  entries: readonly DraftEntry[],
  s0: number,
  e0: number,
  s1: number,
  e1: number,
): DraftEntry[] {
  return entries.map((e) => ({
    ...e,
    sets: e.sets.map((s) => {
      const completedAt = s.completedAt
        ? rescaleCompletedAt(s.completedAt, s0, e0, s1, e1)
        : s.completedAt;
      const dropset = s.dropset
        ? {
            ...s.dropset,
            steps: s.dropset.steps.map((step) => ({
              ...step,
              completedAt: step.completedAt
                ? rescaleCompletedAt(step.completedAt, s0, e0, s1, e1)
                : step.completedAt,
            })),
          }
        : s.dropset;
      return { ...s, completedAt, dropset };
    }),
  }));
}
